import { Link } from 'react-router'
import { Eyebrow, Heading } from '#app/components/brand/section.tsx'
import { type ContentDoc } from '#app/utils/content.ts'
import { Footer } from './footer'
import Header from './header'

function CardGraphic() {
	return (
		<div className="border-hairline relative aspect-[1200/630] overflow-hidden border-b bg-gradient-to-br from-[#F3F0FF] to-[#FBF9F5]">
			<svg
				width="100%"
				height="100%"
				viewBox="0 0 300 130"
				preserveAspectRatio="xMidYMid slice"
				className="absolute inset-0 opacity-85"
			>
				<g stroke="#5B4BFF" strokeWidth="1" opacity="0.3">
					<line x1="150" y1="65" x2="40" y2="20" />
					<line x1="150" y1="65" x2="260" y2="30" />
					<line x1="150" y1="65" x2="70" y2="110" />
					<line x1="150" y1="65" x2="250" y2="105" />
				</g>
				<g fill="#7A5CFF">
					<circle cx="40" cy="20" r="4" />
					<circle cx="260" cy="30" r="5" />
					<circle cx="70" cy="110" r="4" />
					<circle cx="250" cy="105" r="4" />
				</g>
				<circle cx="150" cy="65" r="8" fill="#5B4BFF" />
			</svg>
		</div>
	)
}

function EntryCard({
	entry,
	basePath,
}: {
	entry: ContentDoc
	basePath: string
}) {
	return (
		<Link
			to={`${basePath}/${entry.slug}`}
			className="border-hairline hover:border-brand text-ink block overflow-hidden rounded-2xl border bg-[#FDFCFA] transition-colors"
		>
			{entry.image ? (
				<img
					src={entry.image}
					alt={entry.title}
					width={1200}
					height={630}
					loading="lazy"
					className="border-hairline aspect-[1200/630] w-full border-b object-cover"
				/>
			) : (
				<CardGraphic />
			)}
			<div className="p-5">
				{entry.tag ? (
					<div className="text-brand mb-2.5 font-mono text-[11px] font-semibold tracking-[0.05em] uppercase">
						{entry.tag}
					</div>
				) : null}
				<div className="font-display text-[17px] leading-tight font-semibold text-pretty">
					{entry.title}
				</div>
				{entry.readingTime ? (
					<div className="mt-2 text-[13px] text-[#8A867D]">
						{entry.readingTime}
					</div>
				) : null}
			</div>
		</Link>
	)
}

/**
 * Listing page shared by /guides and /best-tools.
 *
 * Pass `groups` to render the hub-and-spoke structure as labelled sections;
 * omit it (as /best-tools does) for a single flat grid. Grouping exists so the
 * topic architecture is legible to a cold visitor and to a crawler, not just to
 * a reader already inside an article following `related:` links.
 */
export function ContentIndex({
	eyebrow,
	title,
	description,
	basePath,
	entries,
	groups,
	emptyLabel = 'The first pieces are being written now. Check back soon.',
}: {
	eyebrow: string
	title: string
	description: string
	basePath: string
	entries: ContentDoc[]
	groups?: { title: string; description?: string; docs: ContentDoc[] }[]
	emptyLabel?: string
}) {
	return (
		<div className="bg-canvas text-ink flex min-h-screen flex-col font-sans">
			<Header />
			<main className="mx-auto w-full max-w-[1200px] flex-1 px-8 py-16">
				<div className="mb-12 max-w-[640px]">
					<Eyebrow className="mb-3.5">{eyebrow}</Eyebrow>
					<Heading
						as="h1"
						className="text-[clamp(34px,5vw,48px)] leading-[1.05] tracking-[-0.03em]"
					>
						{title}
					</Heading>
					<p className="mt-4 text-xl leading-relaxed text-pretty text-[#57565E]">
						{description}
					</p>
				</div>

				{entries.length === 0 ? (
					<p className="text-[15px] text-[#8A867D]">{emptyLabel}</p>
				) : groups?.length ? (
					<div className="grid gap-16">
						{groups.map((group) => (
							<section key={group.title} aria-label={group.title}>
								<div className="border-hairline mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b pb-3">
									<h2 className="font-display text-ink text-[clamp(20px,2.6vw,26px)] font-semibold tracking-[-0.01em]">
										{group.title}
									</h2>
									{group.description ? (
										<p className="text-[14px] text-[#8A867D]">
											{group.description}
										</p>
									) : null}
								</div>
								<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
									{group.docs.map((entry) => (
										<EntryCard
											key={entry.slug}
											entry={entry}
											basePath={basePath}
										/>
									))}
								</div>
							</section>
						))}
					</div>
				) : (
					<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
						{entries.map((entry) => (
							<EntryCard key={entry.slug} entry={entry} basePath={basePath} />
						))}
					</div>
				)}
			</main>
			<Footer />
		</div>
	)
}
